import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface StorageLockScreenProps {
  storageUsedMb: number;
  storageLimitMb: number;
  memoryCount: number;
}

/**
 * Full-screen overlay shown when the user's storage is full.
 * Rendered above everything in MobileShell. No dismiss on first render —
 * "Free up space" path allows a one-session bypass.
 */
const StorageLockScreen = ({
  storageUsedMb,
  storageLimitMb,
  memoryCount,
}: StorageLockScreenProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [emberLoading, setEmberLoading] = useState(false);

  // Card 1 path: session-only bypass, sends user to oldest memories
  const handleFreeUpSpace = () => {
    setDismissed(true);
    navigate("/feed?sort=oldest");
  };

  const handleEmberUpgrade = async () => {
    setEmberLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "create-checkout-session",
        { body: { tier_id: "ember" } },
      );
      if (error || !data?.url) throw new Error("No checkout URL");
      window.location.href = data.url as string;
    } catch {
      toast.error(t("upgrade.error.checkout"));
    } finally {
      setEmberLoading(false);
    }
  };

  if (dismissed) return null;

  return (
    <div
      className="absolute inset-0 z-[70] flex flex-col overflow-y-auto"
      style={{ backgroundColor: "var(--background, #FDF6EF)" }}
    >
      <div className="flex flex-col items-center gap-6 px-5 pt-12 pb-10 w-full max-w-[430px] mx-auto">

        {/* Icon */}
        <span style={{ fontSize: 56, lineHeight: 1 }}>🔥</span>

        {/* Heading */}
        <div className="text-center space-y-3">
          <h1
            className="text-foreground"
            style={{ fontSize: 20, fontWeight: 500 }}
          >
            {t("storage.lock.heading")}
          </h1>
          <p
            className="text-muted-foreground"
            style={{ fontSize: 14, lineHeight: 1.7 }}
          >
            {t("storage.lock.body", { mb: Math.round(storageUsedMb), count: memoryCount })}
          </p>
        </div>

        {/* Divider */}
        <div className="w-full border-t border-border/50" />

        {/* Option cards */}
        <div className="flex flex-col gap-3 w-full">

          {/* Card 1 — Free path */}
          <div
            className="flex flex-col gap-3 p-4"
            style={{
              border: "1px solid hsl(var(--border) / 0.6)",
              borderRadius: 12,
            }}
          >
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t("storage.lock.freeUp.title")}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                {t("storage.lock.freeUp.body")}
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={handleFreeUpSpace}>
              {t("storage.lock.freeUp.button")}
            </Button>
          </div>

          {/* Card 2 — Ember (hidden tier, storage-only plan) */}
          <div
            className="flex flex-col gap-3 p-4"
            style={{
              border: "1px solid hsl(var(--border) / 0.6)",
              borderRadius: 12,
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-foreground">
                {t("storage.lock.ember.title")}
              </p>
              <span
                className="shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5"
                style={{
                  backgroundColor: "#FDF0EE",
                  color: "#8B3A2E",
                  whiteSpace: "nowrap",
                }}
              >
                {t("storage.lock.ember.badge")}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t("storage.lock.ember.body")}
            </p>
            <Button
              className="w-full"
              onClick={handleEmberUpgrade}
              disabled={emberLoading}
            >
              {emberLoading ? t("storage.lock.ember.loading") : t("storage.lock.ember.button")}
            </Button>
          </div>

          {/* Card 3 — Keeper upgrade */}
          <div
            className="flex flex-col gap-3 p-4"
            style={{
              border: "2px solid hsl(var(--primary))",
              borderRadius: 12,
            }}
          >
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t("storage.lock.keeper.title")}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                {t("storage.lock.keeper.body")}
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() => navigate("/upgrade")}
            >
              {t("storage.lock.keeper.button")}
            </Button>
          </div>

        </div>

        {/* Footer note */}
        <p
          className="text-center text-muted-foreground"
          style={{ fontSize: 12, lineHeight: 1.6 }}
        >
          {t("storage.lock.footer")}
        </p>
      </div>
    </div>
  );
};

export default StorageLockScreen;
